import { useCallback, useEffect, useRef, useState } from 'react';
import { debounce, noop } from 'lodash';
import { ToastAlert, useToast } from '@frameio/vapor';
import TEXT from './generated/TEXT/useCopyText';

const DEFAULT_TIMEOUT = 2000;

TEXT.DEFINE({
  /** Toast message shown when text is successfully copied to the clipboard */
  successText: 'Text copied to clipboard',
});

type CopyTextArgs = {
  displaySuccessToast?: boolean;
  onCopy?: (text?: string) => void;
  toastTimeout?: number;
};

export async function hasClipboardWritePermissions() {
  try {
    const results = await navigator.permissions.query({
      name: 'clipboard-write' as PermissionName,
    });
    return results.state === 'granted' || results.state === 'prompt';
  } catch (error) {
    // NotAllowedError: The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.
    return false;
  }
}

export async function copyTextToClipboard(text: string): Promise<void> {
  const blob = new Blob([text], { type: 'text/plain' });
  return navigator.clipboard.write([
    new ClipboardItem({
      'text/plain': new Promise((resolve) => {
        resolve(blob);
      }),
    }),
  ]);
}

export const useCopyText = ({
  displaySuccessToast = false,
  onCopy = noop,
  toastTimeout = DEFAULT_TIMEOUT,
}: CopyTextArgs = {}) => {
  const [textWasRecentlyCopied, setTextWasRecentlyCopied] = useState(false);
  const timeout = useRef<number | undefined>(undefined);

  const { addToast } = useToast();

  // Extract TEXT properties to avoid calling hooks inside useCallback
  const textSuccessText = TEXT.successText;

  const copyText = useCallback(
    async (text: string, toastLabel = textSuccessText) => {
      if (textWasRecentlyCopied) {
        return;
      }

      await copyTextToClipboard(text);
      onCopy(text);

      if (displaySuccessToast) {
        addToast(
          (closeToast, toastCloseLabel) => (
            <ToastAlert
              closeLabel={toastCloseLabel}
              onRequestClose={closeToast}
              title={toastLabel}
              variant="success"
            />
          ),
          { duration: toastTimeout, placement: 'top' }
        );
      }

      setTextWasRecentlyCopied(true);

      timeout.current = window.setTimeout(() => {
        setTextWasRecentlyCopied(false);
      }, toastTimeout);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      addToast,
      displaySuccessToast,
      onCopy,
      textWasRecentlyCopied,
      toastTimeout,
      textSuccessText,
    ]
  );

  const debouncedCopyText = debounce(copyText, toastTimeout, {
    leading: true,
  });

  useEffect(() => {
    return () => clearTimeout(timeout.current);
  }, []);

  return { copyText: debouncedCopyText, textWasRecentlyCopied };
};
